import { useState, useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { validator, isValid, errors } from 'react-validator';

import Button from 'components/Button/Button';
import Input from 'components/Input/Text/Input';
import Errors from 'components/Errors/Errors';
import { useAppSelector } from 'redux/hooks';
import {
  selectUserPrivateData,
  selectUserData,
  upserUserData,
  fetchUserPrivateData,
} from 'redux/editProfile/userProfile.slices';

import './PersonalInformation.scoped.scss';
import LanguageDropDown from 'components/Header/components/LanguageDropDown/LanguageDropDown';

const PersonalInformation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [profileData, setProfileData] = useState<any>({});
  const userPrivateData = useAppSelector(selectUserPrivateData);
  const userData = useAppSelector(selectUserData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  useEffect(() => {
    if (userPrivateData !== null) {
      setProfileData({
        user: {
          first_name: userData?.first_name,
          last_name: userData?.last_name,
        },
        adress: {
          area: userPrivateData?.private_adresses?.[0]?.area,
          city: userPrivateData?.private_adresses?.[0]?.city,
          country: userPrivateData?.private_adresses?.[0]?.country,
          postal_code: userPrivateData?.private_adresses?.[0]?.postal_code,
          street: userPrivateData?.private_adresses?.[0]?.street,
        },
        phone_number: {
          country_code: userPrivateData?.private_phone_numbers?.[0]?.country_code,
          number: userPrivateData?.private_phone_numbers?.[0]?.number,
        },
      });
    }
  }, [userPrivateData, userData]);

  useEffect(() => {
    dispatch(fetchUserPrivateData());
  }, []);

  function handleUpdate() {
    setValidationErrors({});
    validator(
      profileData,
      {
        'user.first_name': ['required', 'regex:^[A-Za-z - Šš Žž Õõ Ää Öö Üü]+$'],
        'user.last_name': ['required', 'regex:^[A-Za-z - Šš Žž Õõ Ää Öö Üü]+$'],
        'phone_number.country_code': ['required', 'regex:[+]\\d{3}$'],
        'phone_number.number': ['required', 'regex:^[0-9]+$'],
        'adress.area': ['required', 'regex:^[A-Za-z - Šš Žž Õõ Ää Öö Üü]+$'],
        'adress.city': ['required', 'regex:^[A-Za-z - Šš Žž Õõ Ää Öö Üü]+$'],
        'adress.country': ['required', 'regex:^[A-Za-z - Šš Žž Õõ Ää Öö Üü]+$'],
        'adress.postal_code': ['required', 'regex:[A-Za-z\\d -]+'],
        'adress.street': ['required', 'regex:^[A-Za-z - Šš Žž Õõ Ää Öö Üü]+$'],
      },
      {
        'user.first_name.required': t('error:firstname_req'),
        'user.first_name.regex': t('error:firstname_regex'),
        'user.last_name.required': t('error:lastname_req'),
        'user.last_name.regex': t('error:lastname_regex'),
        'phone_number.country_code.required': t('error:phone_country_code_req'),
        'phone_number.country_code.regex': t('error:phone_country_code_regex'),
        'phone_number.number.required': t('error:phone_number_req'),
        'phone_number.number.regex': t('error:phone_number_regex'),
        'adress.area.required': t('error:area_req'),
        'adress.area.regex': t('error:area_regex'),
        'adress.city.required': t('error:city_req'),
        'adress.city.regex': t('error:city_regex'),
        'adress.country.required': t('error:country_req'),
        'adress.country.regex': t('error:country_regex'),
        'adress.street.required': t('error:street_req'),
        'adress.street.regex': t('error:street_regex'),
        'adress.postal_code.required': t('error:postal_code_req'),
        'adress.postal_code.regex': t('error:postal_code_regex'),
      }
    );
    setValidationErrors(errors());
    if (!isValid()) {
      return;
    }
    dispatch(upserUserData(profileData));
  }

  const inputStyle = {
    backgroundColor: '#fff',
    border: '1px solid #213960',
  };

  return (
    <>
      <div className="input-container">
        <Input
          placeholder={t('common:PersonalInformation.First_Name')}
          label={t('common:PersonalInformation.First_Name')}
          grey
          value={profileData?.user?.first_name}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              user: {
                ...prevState.user,
                first_name: value,
              },
            }))
          }
          type="text"
          style={inputStyle}
        />
      </div>
      <Errors errors={validationErrors} prop="user.first_name" />

      <div className="input-container">
        <Input
          placeholder={t('common:PersonalInformation.Last_Name')}
          label={t('common:PersonalInformation.Last_Name')}
          grey
          value={profileData?.user?.last_name}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              user: {
                ...prevState.user,
                last_name: value,
              },
            }))
          }
          type="text"
          style={inputStyle}
        />
      </div>
      <Errors errors={validationErrors} prop="user.last_name" />

      <div className="input-container">
        <Input
          placeholder={t('common:PersonalInformation.Country_code')}
          label={t('common:PersonalInformation.Country_code')}
          grey
          value={profileData?.phone_number?.country_code}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              phone_number: {
                ...prevState.phone_number,
                country_code: value,
              },
            }))
          }
          type="text"
          style={{ width: '96%', backgroundColor: '#fff', border: '1px solid #213960' }}
        />
        <Input
          placeholder={t('common:PersonalInformation.Telephone_Number')}
          label={t('common:PersonalInformation.Telephone_Number')}
          grey
          value={profileData?.phone_number?.number}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              phone_number: {
                ...prevState.phone_number,
                number: value,
              },
            }))
          }
          type="text"
          style={inputStyle}
        />
      </div>
      <div className="full-width">
        <Errors errors={validationErrors} prop="phone_number.country_code" />
        <Errors errors={validationErrors} prop="phone_number.number" />
      </div>
      <div className="input-container">
        <Input
          placeholder={t('common:PersonalInformation.Area')}
          label={t('common:PersonalInformation.Area')}
          grey
          value={profileData?.adress?.area}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              adress: {
                ...prevState.adress,
                area: value,
              },
            }))
          }
          type="text"
          style={{ width: '94%', backgroundColor: '#fff', border: '1px solid #213960' }}
        />
        <Input
          placeholder={t('common:PersonalInformation.City')}
          label={t('common:PersonalInformation.City')}
          grey
          value={profileData?.adress?.city}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              adress: {
                ...prevState.adress,
                city: value,
              },
            }))
          }
          type="text"
          style={{ width: '94%', backgroundColor: '#fff', border: '1px solid #213960' }}
        />
        <Input
          placeholder={t('common:PersonalInformation.Country')}
          label={t('common:PersonalInformation.Country')}
          grey
          value={profileData?.adress?.country}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              adress: {
                ...prevState.adress,
                country: value,
              },
            }))
          }
          type="text"
          style={inputStyle}
        />
      </div>
      <div className="full-width">
        <Errors errors={validationErrors} prop="adress.area" />
        <Errors errors={validationErrors} prop="adress.city" />
        <Errors errors={validationErrors} prop="adress.country" />
      </div>
      <div className="input-container">
        <Input
          placeholder={t('common:PersonalInformation.PostalCode')}
          label={t('common:PersonalInformation.PostalCode')}
          grey
          value={profileData?.adress?.postal_code}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              adress: {
                ...prevState.adress,
                postal_code: value,
              },
            }))
          }
          type="text"
          style={{ width: '96%', backgroundColor: '#fff', border: '1px solid #213960' }}
        />
        <Input
          placeholder={t('common:PersonalInformation.Street')}
          label={t('common:PersonalInformation.Street')}
          grey
          value={profileData?.adress?.street}
          onChange={value =>
            setProfileData(prevState => ({
              ...prevState,
              adress: {
                ...prevState.adress,
                street: value,
              },
            }))
          }
          type="text"
          style={inputStyle}
        />
      </div>
      <div className="input-container">
        <LanguageDropDown />
      </div>

      <div className="full-width">
        <Errors errors={validationErrors} prop="adress.postal_code" />
        <Errors errors={validationErrors} prop="adress.street" />
      </div>
      <div className="input-container" />
      <Button
        onClick={handleUpdate}
        round
        outlined
        style={{
          backgroundColor: '#5CD79E',
          width: '150px',
          color: '#fff',
          opacity: 0.7,
          fontSize: 15,
          marginTop: 30,
          marginBottom: 30,
        }}
      >
        <p>{t('common:save')}</p>
      </Button>
    </>
  );
};

export default PersonalInformation;
